/**
 * Zone 5: Golden Summit Decorator
 *
 * Handles decoration for the Golden Summit zone (Zone 5)
 * Includes 7 decoration types: 5 top-side + 2 underside
 *
 * Top decorations: gold-coin, gem-crystal, golden-sparkle, trophy-piece, crown-fragment
 * Underside decorations: dripping-lava, smoke-puffs
 *
 * PHASE 7C: Converted from Graphics to Sprites
 */

import Phaser from 'phaser';
import { PlatformDecoratorBase, DecorationConfig } from './PlatformDecoratorBase';
import { DecorationTextureGenerator } from '../DecorationTextureGenerator'; // PHASE 7C

export class Zone5Decorator extends PlatformDecoratorBase {
  protected getDecorations(): DecorationConfig[] {
    return [
      // Top-side decorations
      {
        type: 'gold-coin',
        chance: 0.2,
        minWidth: 45,
        placement: 'top',
      },
      {
        type: 'gem-crystal',
        chance: 0.18,
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'golden-sparkle',
        chance: 0.25,
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'trophy-piece',
        chance: 0.1,
        minWidth: 60,
        placement: 'top',
      },
      {
        type: 'crown-fragment',
        chance: 0.08,
        minWidth: 65,
        placement: 'top',
      },
      // Underside decorations
      {
        type: 'dripping-lava',
        chance: 0.4,
        minWidth: 50,
        placement: 'underside',
      },
      {
        type: 'smoke-puffs',
        chance: 0.35,
        minWidth: 60,
        placement: 'underside',
      },
    ];
  }

  /**
   * Add a specific decoration to the platform
   * PHASE 7C: CONVERTED TO SPRITES (was Graphics)
   */
  protected addDecoration(
    platformContainer: Phaser.GameObjects.Container,
    decorationType: string,
    _placement: 'top' | 'underside',
    width: number,
    height: number
  ): void {
    // PHASE 7C: Create sprite from pre-generated texture
    const textureKey = DecorationTextureGenerator.getTextureKey(decorationType);
    const sprite = this.scene.add.sprite(0, 0, textureKey);
    sprite.setOrigin(0.5, 0.5);
    platformContainer.add(sprite);

    const position = this.calculateDecorationPosition(decorationType, width, height);
    sprite.setPosition(position.x, position.y);

    const animationType = this.getAnimationType(decorationType);
    if (animationType) {
      this.animatedElements.push({
        sprite,
        type: animationType,
        animationPhase: this.random.nextFloat(0, Math.PI * 2),
        animationSpeed: this.getAnimationSpeed(animationType),
      });
    }
  }

  private calculateDecorationPosition(
    type: string,
    width: number,
    height: number
  ): { x: number; y: number } {
    // CRITICAL FIX: Only use platform-dependent positioning (constant offsets in texture)
    switch (type) {
      case 'gold-coin':
        return {
          x: this.random.nextFloat(-width * 0.35, width * 0.35),
          y: -height / 2, // Texture has the -8 offset
        };
      case 'gem-crystal':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the -6 offset
        };
      case 'golden-sparkle':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: -height / 2, // Texture has the random -8 to -18 offset
        };
      case 'trophy-piece':
        return {
          x: this.random.nextFloat(-width * 0.2, width * 0.2),
          y: -height / 2, // Texture has the -8 offset
        };
      case 'crown-fragment':
        return {
          x: this.random.nextFloat(-width * 0.15, width * 0.15),
          y: -height / 2, // Texture has the -10 offset
        };
      case 'dripping-lava':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: height / 2,
        };
      case 'smoke-puffs':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: height / 2,
        };
      default:
        return { x: 0, y: 0 };
    }
  }

  private getAnimationType(type: string): string | null {
    switch (type) {
      case 'golden-sparkle':
        return 'sparkle-pulse';
      case 'dripping-lava':
        return 'lava-pulse';
      case 'smoke-puffs':
        return 'smoke-rise';
      default:
        return null;
    }
  }

  private getAnimationSpeed(type: string): number {
    switch (type) {
      case 'sparkle-pulse':
        return 0.12;
      case 'lava-pulse':
        return 0.05;
      case 'smoke-rise':
        return 0.03;
      default:
        return 0.05;
    }
  }
}
